import {
  type ApiBlock,
  type TextractDocument,
} from "amazon-textract-response-parser";
import { useCallback, useEffect, useRef, useState } from "react";

import { type ContextMenuData, type SelectedData } from "./";
import { useTemplate } from "~/hooks/use-template";
import useDebounce from "~/hooks/use-debounce";

interface ICanvasComponentProps {
  imageUrls: string[];
  contextMenuData: ContextMenuData[] | undefined;
  textractAllData: TextractDocument[];
  enableRowSelection: boolean;
  table_block_id?: string;
  activeColumns?: ContextMenuData[];
  onSelect: ({
    columnIndex,
    table_block_id,
    headerText,
    tagid,
  }: SelectedData) => void;
  setCanvasLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

// type CustomApiBlock = ApiBlock & {
//   Text: string;
// };

export interface CustomBlock {
  left: number;
  top: number;
  width: number;
  height: number;
  columnIndex?: number;
  apiBlock?: ApiBlock;
}

export default function CanvasComponent({
  imageUrls,
  contextMenuData,
  textractAllData,
  enableRowSelection,
  table_block_id,
  activeColumns,
  onSelect,
  setCanvasLoading,
}: ICanvasComponentProps) {
  const [selectedColumns, setSelectedColumns] = useState<ContextMenuData[]>(
    activeColumns ?? []
  );
  const [, /* currentColumn */ setCurrentColumn] = useState<{
    columnIndex: number;
    text: string;
  } | null>();
  const tooltipRefs = useRef<{ bounds: DOMRect; parent: HTMLElement } | null>();
  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const canvasRefs = useRef<HTMLCanvasElement[]>([]);
  const imageRefs = useRef<HTMLImageElement[]>([]);
  const [hoveredColumn, setHoveredColumn] = useState<{
    column: number;
    row: number;
  }>({ column: 0, row: 0 });
  const { selectedField } = useTemplate();
  const [currentTableBlockId, setCurrentTableBlockId] = useState<string>();
  const [contextRef, setContextRef] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const drawActiveColumns = useCallback(() => {
    imageRefs.current.forEach((_, index) => {
      const canvas = canvasRefs.current[index];
      const context = canvas?.getContext("2d");
      if (canvas != null && context != null) {
        const tables =
          table_block_id && table_block_id !== "null"
            ? textractAllData[0]?._pages[0]._tables.filter(
                (table) => table.id === table_block_id
              )
            : textractAllData[index]?._pages[0]._tables;
        // console.log(tables);
        if (tables !== null) {
          tables.forEach((table) => {
            const allColumns: number[] = [];
            table._cells.map((cell) => {
              if (allColumns.includes(cell.columnIndex)) return;
              allColumns.push(cell.columnIndex);
            });
            allColumns.forEach((columnIndex) => {
              const columns = table._cells.filter(
                (cell) => cell.columnIndex === columnIndex
              );
              if (columns.length > 0) {
                const { left, top, width } = columns[0]._geometry._boundingBox;
                const cLeft = left * canvas.width;
                const cTop = top * canvas.height;
                const cWidth = width * canvas.width;
                context.beginPath();
                const isSelected = selectedColumns.some(
                  (sc) => sc.columnIndex === columnIndex
                );
                context.strokeStyle = isSelected ? "blue" : "gray";
                context.lineWidth = 1;
                if (isSelected) {
                  context.fillStyle = "#0808ff17";
                  context.fillRect(
                    cLeft,
                    cTop,
                    cWidth,
                    table._geometry._boundingBox.height * canvas.height
                  );
                }
                context.rect(
                  cLeft,
                  cTop,
                  cWidth,
                  table._geometry._boundingBox.height * canvas.height
                );
                context.strokeRect(
                  cLeft,
                  cTop,
                  cWidth,
                  table._geometry._boundingBox.height * canvas.height
                );
                context.closePath();
              }
            });
          });
        }
      }
    });
  }, [table_block_id, textractAllData, selectedColumns]);

  useEffect(() => {
    setSelectedColumns(activeColumns ?? []);

    drawActiveColumns();
  }, [activeColumns, drawActiveColumns]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      imageRefs.current.forEach((img, index) => {
        const canvas = canvasRefs.current[index];
        const context = canvas?.getContext("2d");
        const image = img;
        const rect = canvas?.getBoundingClientRect();
        if (rect == null) return;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (canvas != null && context != null) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);

          drawActiveColumns();

          const tables = table_block_id
            ? textractAllData[0]?._pages[0]._tables.filter(
                (table) => table.id === table_block_id
              )
            : textractAllData[index]?._pages[0]._tables;
          tables.forEach((table) => {
            table._cells.forEach((cell) => {
              const { left, width, top, height } = cell._geometry._boundingBox;
              const cLeft = left * canvas.width;
              const cWidth = width * canvas.width;
              const cTop = top * canvas.height;
              const cHeight = height * canvas.height;
              context.beginPath();
              if (enableRowSelection) {
                context.rect(cLeft, cTop, cWidth, cHeight);
              } else {
                context.rect(
                  cLeft,
                  table._geometry._boundingBox.top * canvas.height,
                  cWidth,
                  table._geometry._boundingBox.height * canvas.height
                );
              }
              context.strokeStyle = "red";
              context.lineWidth = 2;
              if (context.isPointInPath(x, y)) {
                const isSelected = selectedColumns.find(
                  (sc) => sc.columnIndex === cell.columnIndex
                );
                if (isSelected != null && !enableRowSelection) {
                  setCurrentColumn({
                    columnIndex: cell.columnIndex,
                    text: isSelected.text,
                  });
                } else {
                  setCurrentColumn(null);
                }
                setHoveredColumn({
                  column: cell.columnIndex,
                  row: cell.rowIndex,
                });
                setCurrentTableBlockId(table.id);
                if (enableRowSelection) {
                  context.lineWidth = 2;

                  context.strokeRect(cLeft, cTop, cWidth, cHeight);
                } else {
                  tooltipRefs.current = {
                    bounds: new DOMRect(
                      cLeft,
                      table._geometry._boundingBox.top * canvas.height,
                      cWidth,
                      table._geometry._boundingBox.height * canvas.height
                    ),
                    parent: canvas,
                  };
                  context.strokeRect(
                    cLeft,
                    table._geometry._boundingBox.top * canvas.height,
                    cWidth,
                    table._geometry._boundingBox.height * canvas.height
                  );
                }
              }
              context.closePath();
            });
          });
        }
      });
    },
    [textractAllData, selectedColumns, enableRowSelection, drawActiveColumns]
  );

  const handleContextMenu = useCallback((event: MouseEvent) => {
    event.preventDefault();
    setContextRef({ x: event.clientX, y: event.clientY });
    setShowContextMenu(true);
  }, []);

  const handleMouseClick = useCallback(
    useDebounce((event: MouseEvent) => {
      event.preventDefault();
      drawActiveColumns();
      if (showContextMenu) {
        setShowContextMenu(false);
      }
      imageRefs.current.forEach((img, index) => {
        const canvas = canvasRefs.current[index];
        const context = canvas?.getContext("2d");
        const image = img;
        const rect = canvas?.getBoundingClientRect();
        if (rect == null) return;
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        if (canvas != null && context != null) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);

          const tables = table_block_id
            ? textractAllData[0]?._pages[0]._tables.filter(
                (table) => table.id === table_block_id
              )
            : textractAllData[index]?._pages[0]._tables;
          tables.forEach((table) => {
            let found = false;
            table._cells.forEach((cell) => {
              const { left, width, top, height } = cell._geometry._boundingBox;
              const cLeft = left * canvas.width;
              const cWidth = width * canvas.width;
              const cTop = top * canvas.height;
              const cHeight = height * canvas.height;
              context.beginPath();
              if (enableRowSelection) {
                context.rect(cLeft, cTop, cWidth, cHeight);
              } else {
                context.rect(
                  cLeft,
                  table._geometry._boundingBox.top * canvas.height,
                  cWidth,
                  table._geometry._boundingBox.height * canvas.height
                );
              }
              context.strokeStyle = "red";
              context.lineWidth = 2;
              if (context.isPointInPath(x, y) && !found) {
                found = true;
                // if (enableRowSelection) {
                onSelect({
                  tagid: selectedField?.field,
                  columnIndex: cell.columnIndex,
                  table_block_id: table.id,
                  headerText:
                    table._cells.find(
                      (celr) =>
                        celr.rowIndex === cell.rowIndex &&
                        celr.columnIndex === cell.columnIndex
                    )?.text ?? "",
                  ...(enableRowSelection && { rowIndex: cell.rowIndex }),
                });
                // }
                const isSelected = selectedColumns.find(
                  (sc) => sc.columnIndex === cell.columnIndex
                );
                if (isSelected != null && !enableRowSelection) {
                  setCurrentColumn({
                    columnIndex: cell.columnIndex,
                    text: isSelected.text,
                  });
                } else {
                  setCurrentColumn(null);
                }
                setHoveredColumn({
                  column: cell.columnIndex,
                  row: cell.rowIndex,
                });
                setCurrentTableBlockId(table.id);
                if (enableRowSelection) {
                  context.lineWidth = 2;

                  context.strokeRect(cLeft, cTop, cWidth, cHeight);
                } else {
                  tooltipRefs.current = {
                    bounds: new DOMRect(
                      cLeft,
                      table._geometry._boundingBox.top * canvas.height,
                      cWidth,
                      table._geometry._boundingBox.height * canvas.height
                    ),
                    parent: canvas,
                  };
                  context.strokeRect(
                    cLeft,
                    table._geometry._boundingBox.top * canvas.height,
                    cWidth,
                    table._geometry._boundingBox.height * canvas.height
                  );
                }
              }
              context.closePath();
            });
          });
        }
      });
    }, 300),
    [
      currentTableBlockId,
      drawActiveColumns,
      enableRowSelection,
      onSelect,
      selectedColumns,
      selectedField?.field,
      showContextMenu,
      textractAllData,
    ]
  );

  useEffect(() => {
    const drawImageAndBlocks = (
      canvas: HTMLCanvasElement,
      context: CanvasRenderingContext2D,
      image: HTMLImageElement
    ) => {
      if (canvas != null && context != null) {
        const parentWidth = canvas?.parentElement?.offsetWidth;
        const scale = parentWidth! / image.width;
        const scaledHeight = image.height * scale;
        canvas.height = scaledHeight;
        canvas.width = Math.min(parentWidth!, image.width);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        drawActiveColumns();
      }
    };
    imageUrls.forEach((url, index) => {
      const canvas = canvasRefs.current[index];
      const context = canvas?.getContext("2d");

      if (canvas != null && context != null) {
        const image = new Image();
        image.onload = () => {
          drawImageAndBlocks(canvas, context, image);
          if (setCanvasLoading) {
            setCanvasLoading(false);
          }
        };
        image.src = url;
        imageRefs.current[index] = image;
      }

      canvas?.addEventListener("mousemove", handleMouseMove);
      canvas?.addEventListener("contextmenu", handleContextMenu);
      canvas?.addEventListener("click", handleMouseClick);
      return () => {
        canvas?.removeEventListener("mousemove", handleMouseMove);
        canvas?.removeEventListener("contextmenu", handleContextMenu);
        canvas?.removeEventListener("click", handleMouseClick);
      };
    });
  }, [
    imageUrls,
    textractAllData,
    handleMouseMove,
    enableRowSelection,
    handleContextMenu,
    handleMouseClick,
    drawActiveColumns,
  ]);

  return (
    <>
      {showContextMenu && hoveredColumn != null && (
        <>
          <ul
            className="bg-white py-3 px-4 rounded-lg shadow-xl absolute z-50 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-300"
            style={{
              top: contextRef.y,
              left: contextRef.x,
            }}
          >
            {contextMenuData?.map((item) => {
              console.log(item);
              return (
                <li
                  className="cursor-pointer hover:bg-blue-400 p-2 flex gap-2 items-center"
                  key={item.id}
                  onClick={() => {
                    setSelectedColumns((prevColumns) => {
                      const newColumns = [...prevColumns];
                      if (newColumns.map((sc) => sc.id).includes(item.id)) {
                        newColumns.splice(newColumns.indexOf(item), 1);
                      } else {
                        newColumns.push({
                          ...item,
                          columnIndex: hoveredColumn.column,
                        });
                      }
                      let headerText = "";
                      textractAllData.forEach((td) =>
                        td._pages[0]._tables.forEach((table) => {
                          if (table.id !== table_block_id) return;
                          headerText =
                            table._cells.find(
                              (cell) =>
                                cell.columnIndex === hoveredColumn.column &&
                                cell.rowIndex === 1
                            )?.text ?? "";
                        })
                      );

                      const data: SelectedData & { headerText: string } = {
                        tagid: item.id,
                        columnIndex: hoveredColumn.column,
                        table_block_id: currentTableBlockId!,
                        headerText,
                      };
                      onSelect(data);
                      return newColumns;
                    });
                    setShowContextMenu(false);
                  }}
                >
                  {selectedColumns.map((sc) => sc.id).includes(item.id) && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="size-5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {item.text}
                </li>
              );
            })}
          </ul>
        </>
      )}
      <div className="flex flex-col gap-2 bg-gray-400">
        {imageUrls.map((_, index) => (
          <canvas
            key={index}
            className="block m-auto"
            ref={(el) => {
              if (el != null) {
                canvasRefs.current[index] = el;
              }
            }}
          />
        ))}
      </div>
    </>
  );
}
